import { FINSEMBLECONFIG, toGoLang, toGoLangInvoke, FINSEMBLECONFIGAPPS, FSBLPromise } from "../common/windowFunctions";
import { convertPreferencesToObject } from "./utils";
import { merge, set } from "lodash";
import { logStatus } from "../utils";
import { PromiseWrapper } from "../utils/pw";
import { scheduledRestartOrShutdown, scheduledCloseDiscardUnsavedChanges } from "./preferences";
const INVALID_PATH_ERROR_MESSAGE = "Invalid path - MUST be a path array. e.g. for myObject.property use ['myObj', 'property']";
const recursePathArray = (config, pathArray) => {
    if (typeof config !== "object") {
        return undefined;
    }
    if (!pathArray.length) {
        return config;
    }
    const field = pathArray[0];
    if (pathArray.length === 1) {
        return config[field];
    }
    if (!config[field]) {
        return undefined;
    }
    return recursePathArray(config[field], pathArray.slice(1));
};
const strToPathArray = (field) => {
    const pathArray = [];
    const step = (obj) => {
        if (typeof obj !== "object") {
            return;
        }
        const keys = Object.keys(obj);
        if (!keys.length) {
            return;
        }
        pathArray.push(keys[0]);
        step(obj[keys[0]]);
    };
    step(set({}, field, "dummy"));
    return pathArray;
};
const remove = (path, config) => {
    let pointer = config;
    for (let i = 0; i < path.length - 1; i++) {
        const field = path[i];
        if (!pointer[field]) {
            pointer[field] = {};
        }
        pointer = pointer[field];
    }
    delete pointer[path[path.length - 1]];
};
export class ConfigClientImpl {
    async maybeSendCommandToIOCD(params) {
        switch (params.field) {
            case "finsemble.scheduledRestart":
                await scheduledRestartOrShutdown(this.glue, "restart", params.value, this.logStatus);
                break;
            case "finsemble.scheduledShutdown":
                await scheduledRestartOrShutdown(this.glue, "shutdown", params.value, this.logStatus);
                break;
            case "finsemble.scheduledCloseDiscardUnsavedChanges":
                await scheduledCloseDiscardUnsavedChanges(this.glue, params.value, this.logStatus);
                break;
            case "finsemble.initialWorkspace":
                break;
            case "finsemble.servicesConfig.workspace.promptUserOnDirtyWorkspace":
                break;
            case "finsemble.servicesConfig.workspace.discardUnsavedChanges":
                break;
            case "finsemble.servicesConfig.notifications.disableDotOnToolbarIcon":
                break;
            case "finsemble.servicesConfig.notifications.showDotOnToolbarIconForMutedNotifications":
                break;
            case "finsemble.servicesConfig.notifications.toastTimeoutOnActionableNotifications":
                break;
            case "finsemble.servicesConfig.notifications.informationalNotificationToastPeriodSeconds":
                break;
            case "finsemble.servicesConfig.notifications.defaultSnoozePeriodSeconds":
                break;
            case "finsemble.servicesConfig.notifications.newNotificationHaloPeriodSeconds":
                break;
            default:
                return;
        }
    }
    maybeTriggerListeners(data) {
        this.listeners.forEach((entry) => {
            try {
                const { pathArray, listener, config } = entry;
                const oldValue = recursePathArray(config, pathArray);
                const newValue = recursePathArray(data, pathArray);
                if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
                    listener(null, {
                        field: pathArray.join("."),
                        value: newValue
                    });
                }
                entry.config = data;
            }
            catch (e) {
                this.glue.logger.error(`ConfigClient, exception in listener: ${e.toString()}`);
            }
        });
    }
    async waitForUserStage() {
        const inUserStage = await this.glue.contexts.get("IO.Global.InUserStage");
        if (!inUserStage) {
            await this.glue.contexts.set("IO.Global.InUserStage", { inUserStage: false });
        }
        if (!inUserStage?.inUserStage) {
            await this.glue.contexts.subscribe("IO.Global.InUserStage", (context) => {
                if (context.inUserStage) {
                    this.preferencesAreAvailable.resolve();
                }
            });
            await this.preferencesAreAvailable.promise;
        }
        else {
            this.preferencesAreAvailable.resolve();
        }
        this.glue.prefs.subscribeFor("IO.Global.FinsemblePreferences", async (preferences) => {
            this.cachedPreferences = preferences.data;
            const config = await this.glue.contexts.get(FINSEMBLECONFIG);
            this.maybeTriggerListeners(merge(config, preferences.data));
        });
    }
    async initialize() {
        this.waitForUserStage();
    }
    async subscribeToConfigContext() {
        if (this.listeners.length) {
            return;
        }
        await this.glue.contexts.subscribe(FINSEMBLECONFIG, async (data, delta, removed, unsubscribe, extraData) => {
            const preferences = await this.getPreferencesNoWait();
            const prefsAsObject = convertPreferencesToObject(preferences);
            this.maybeTriggerListeners(merge(data, prefsAsObject));
        });
    }
    constructor(gluePromise, asyncConstructors, routerClient, loggerClient) {
        this.gluePromise = gluePromise;
        this.asyncConstructors = asyncConstructors;
        this.routerClient = routerClient;
        this.loggerClient = loggerClient;
        this.cachedPreferences = null;
        this.listeners = [];
        this.preferencesAreAvailable = new PromiseWrapper;
        this.whichContext = (pathArray) => {
            if (pathArray[0] !== "finsemble")
                return FINSEMBLECONFIG;
            if (pathArray[1] === "apps" || pathArray[1] === "components")
                return FINSEMBLECONFIGAPPS;
            return FINSEMBLECONFIG;
        };
        const pw = asyncConstructors.addConstructor();
        this.loggerClient.verbose("ConfigClient Constructor");
        gluePromise.then((g) => {
            this.glue = g;
            this.initialize().finally(() => {
                this.loggerClient.verbose("ConfigClient initialized");
                asyncConstructors.finishConstructor(pw);
            });
        });
        this.logStatus = this.logStatus.bind(this);
    }
    logStatus(method, type, data) {
        logStatus(this.loggerClient, this.glue, { api: "ConfigClient", method, type, data });
    }
    async addListener(params, fn, cb) {
        this.logStatus("addListener", "start", { params });
        await this.asyncConstructors.ready();
        const pathArray = Array.isArray(params) ? params : (params.field ? strToPathArray(params.field) : []);
        const config = await this.glue.contexts.get(FINSEMBLECONFIG);
        await this.subscribeToConfigContext();
        this.listeners.push({
            pathArray,
            listener: fn,
            config
        });
        cb?.();
        this.logStatus("addListener", "return");
    }
    async addListeners(params, fn, cb) {
        this.logStatus("addListeners", "start", params);
        await this.asyncConstructors.ready();
        const promises = params.map((param) => {
            if (!param) {
                return this.addListener({ field: null }, fn);
            }
            else if (typeof param === "string") {
                return this.addListener({ field: param }, fn);
            }
            else if (typeof param.field === "string") {
                return this.addListener({ field: param.field }, param.listener);
            }
            else {
                return this.addListener(param.field, param.listener);
            }
        });
        await FSBLPromise().allSettled(promises);
        cb?.();
        this.logStatus("addListeners", "return");
    }
    async getValue(params, cb) {
        this.logStatus("getValue", "start", params);
        await this.asyncConstructors.ready();
        const field = typeof params === "string" ? params : params.field;
        const { err, data } = await this.get(strToPathArray(field));
        this.logStatus("getValue", "return", { err, data });
        cb?.(err, data);
        return { err, data };
    }
    async getValues(fields, cb) {
        this.logStatus("getValues", "start", fields);
        await this.asyncConstructors.ready();
        if (!fields) {
            const response = this.getManifest(cb);
            this.logStatus("getValues", "return", response);
            return response;
        }
        const fieldNames = fields.map((field) => typeof field === "string" ? field : field.field);
        const promises = fieldNames.map((fieldName) => this.get(strToPathArray(fieldName)));
        const results = await FSBLPromise().allSettled(promises);
        const data = {};
        results.forEach((result, index) => {
            if (result.status === "fulfilled") {
                data[fieldNames[index]] = result.value.data;
            }
        });
        const errors = results.map((result) => result.status === "fulfilled" ? result.value.err : result.reason);
        const err = errors.find((error) => typeof error === "string");
        this.logStatus("getValues", "return", { err, data });
        cb?.(err, data);
        return { err, data };
    }
    async getManifest(cb) {
        this.logStatus("getManifest", "start");
        await this.asyncConstructors.ready();
        const response = this.get([], cb);
        this.logStatus("getManifest", "return", response);
        return response;
    }
    async getPreferences(cb) {
        if (!this.preferencesAreAvailable.resolved) {
            console.warn("ConfigClient.getPreferences() called before having entered UserStage. Waiting...");
        }
        await this.preferencesAreAvailable.promise;
        return this.getPreferencesNoWait(cb);
    }
    async getPreferencesNoWait(cb) {
        this.logStatus("getPreferences", "start");
        await this.asyncConstructors.ready();
        if (!this.preferencesAreAvailable.resolved) {
            return { data: {} };
        }
        if (!this.cachedPreferences) {
            const { err = null, data: tmp } = await toGoLang(() => this.glue.prefs.get("IO.Global.FinsemblePreferences"));
            const data = tmp?.data ?? {};
            this.cachedPreferences = data;
            this.logStatus("getPreferences", "return", { err, data });
            cb?.(err, data);
            return {
                err,
                data
            };
        }
        else {
            this.logStatus("getPreferences", "return", { err: null, data: this.cachedPreferences });
            cb?.(null, this.cachedPreferences);
            return {
                err: null,
                data: this.cachedPreferences
            };
        }
    }
    async processAndSet(params, cb) {
        await this.asyncConstructors.ready();
        const { newConfig, replace, overwrite } = params;
        const { err, data } = await toGoLangInvoke(() => this.glue.interop.invoke("FSBL.Clients.ConfigClient.processAndSet", { newConfig, replace, overwrite }));
        if (!err) {
            this.routerClient.publish("Config.changeNotification", {
                newConfig,
                finsemble: data
            });
        }
        cb?.(err, { data });
        this.logStatus("processAndSet", "return");
        return { err, data };
    }
    removeListener(params, fn, cb) {
        this.logStatus("removeListener", "start", params);
        const asPathArray = Array.isArray(params) ? params : (params.field ? strToPathArray(params.field) : []);
        const isAMatch = (entry) => {
            if (fn) {
                return entry.listener !== fn || entry.pathArray.join(".") !== asPathArray.join(".");
            }
            else {
                return entry.pathArray.join(".") !== asPathArray.join(".");
            }
        };
        this.listeners = this.listeners.filter(isAMatch);
        this.logStatus("removeListener", "return");
        cb?.();
    }
    removeListeners(params, fn, cb) {
        this.logStatus("removeListeners", "start", params);
        const asArray = Array.isArray(params) ? params : [params];
        asArray.map((param) => {
            if (!param) {
                this.removeListener({ field: null }, fn);
            }
            else if (typeof param === "string") {
                this.removeListener({ field: param }, fn);
                return;
            }
            else if (typeof param.field === "string") {
                this.removeListener({ field: param.field }, param.listener);
                return;
            }
            else {
                this.removeListener(param.field, param.listener);
                return;
            }
        });
        this.logStatus("removeListeners", "return");
        cb?.(null, true);
    }
    async removeValue(params, cb) {
        this.logStatus("removeValue", "start", params);
        await this.asyncConstructors.ready();
        const field = typeof params === "string" ? params : params.field;
        const { err, data } = await this.remove(strToPathArray(field));
        this.logStatus("removeValue", "return", { err, data });
        cb?.(err, data);
        return { err, data };
    }
    async removeValues(fields, cb) {
        this.logStatus("removeValues", "start", fields);
        await this.asyncConstructors.ready();
        const fieldNames = fields.map((field) => typeof field === "string" ? field : field.field);
        let config = await this.glue.contexts.get(FINSEMBLECONFIG);
        if (!config) {
            config = {};
        }
        fieldNames.map((fieldName) => remove(strToPathArray(fieldName), config));
        await this.glue.contexts.set(FINSEMBLECONFIG, config);
        this.logStatus("removeValues", "return", { err: null });
        cb?.(null);
        return { err: null };
    }
    async setPreference(params, cb) {
        this.logStatus("setPreference", "start", { params });
        await this.asyncConstructors.ready();
        await this.maybeSendCommandToIOCD(params);
        const { err = null, data } = await toGoLang(() => this.glue.prefs.updateFor("IO.Global.FinsemblePreferences", {
            [params.field]: params.value
        }));
        this.logStatus("setPreference", "return", { err, data });
        cb?.(err, data);
        return { err, data };
    }
    async setValue(params, cb) {
        this.logStatus("setValue", "start", params);
        await this.asyncConstructors.ready();
        const result = await this.set(strToPathArray(params.field), params.value);
        const err = result.err ? result.err.toString() : null;
        this.logStatus("setValue", "return", { err });
        cb?.(err);
        return { err };
    }
    async setValues(fields, cb) {
        this.logStatus("setValues", "start", fields);
        await this.asyncConstructors.ready();
        const promises = fields.map((field) => this.setValue(field));
        const results = await FSBLPromise().allSettled(promises);
        const errors = results.map((result) => result.status === "fulfilled" ? result.value.err : result.reason);
        const err = errors.find((error) => typeof error === "string");
        this.logStatus("setValues", "return", { err });
        cb?.(err);
        return { err };
    }
    async unsetPreference(params, cb) {
        this.logStatus("unsetPreference", "start", { params });
        await this.asyncConstructors.ready();
        const { err, data } = await this.getPreferences();
        if (err) {
            this.logStatus("unsetPreference", "error", { err });
            cb?.(err);
            return { err };
        }
        if (data && Object.keys(data).length > 0) {
            delete data[params.field];
            const { err: err2 } = await toGoLang(() => this.glue.prefs.setFor("IO.Global.FinsemblePreferences", data));
            this.logStatus("unsetPreference", "return", { err: err2, data });
            cb?.(err2);
            return { err: err2 };
        }
        this.logStatus("unsetPreference", "return", { err: null });
        cb?.(null);
        return {
            err: null
        };
    }
    async get(path, cb) {
        this.logStatus("get", "start", path);
        await this.asyncConstructors.ready();
        if (path === null || !Array.isArray(path)) {
            this.logStatus("get", "error", { err: INVALID_PATH_ERROR_MESSAGE });
            cb?.(INVALID_PATH_ERROR_MESSAGE);
            return { err: INVALID_PATH_ERROR_MESSAGE };
        }
        const { err: prefErr, data: preferences = {} } = await this.getPreferencesNoWait();
        const prefObj = convertPreferencesToObject(preferences);
        const config = await this.glue.contexts.get(this.whichContext(path));
        const err = Object.keys(config).length === 0 ? "No config available. Is Finsemble service running?" : null;
        const objectToQuery = merge(config, prefObj);
        const value = recursePathArray(objectToQuery, path);
        const result = {
            err: err ?? prefErr,
            data: value
        };
        this.logStatus("get", "return", result);
        cb?.(result.err, result.data);
        return result;
    }
    async getMultiple(paths, cb) {
        this.logStatus("getMultiple", "start", paths);
        await this.asyncConstructors.ready();
        const err = null;
        const config = await this.glue.contexts.get(FINSEMBLECONFIG);
        const { data: preferences = {} } = await this.getPreferencesNoWait();
        const prefs = convertPreferencesToObject(preferences);
        const results = {};
        paths.forEach((path) => {
            const pref = recursePathArray(prefs, path);
            if (typeof pref !== "undefined") {
                results[path.join(".")] = pref;
                return;
            }
            const value = recursePathArray(config, path);
            if (typeof value !== "undefined") {
                results[path.join(".")] = value;
            }
        });
        const finalResults = Object.keys(results).length ? results : undefined;
        this.logStatus("getMultiple", "return", { err, data: finalResults });
        cb?.(err, finalResults);
        return { err, data: finalResults };
    }
    async set(path, value, cb) {
        this.logStatus("set", "start", { path, value });
        await this.asyncConstructors.ready();
        const pathAsString = path.join(".");
        const { err } = await toGoLang(() => this.glue.contexts.setPath(FINSEMBLECONFIG, pathAsString, value));
        if (err) {
            this.logStatus("set", "error", { subFunction: "this.glue.contexts.update(FINSEMBLECONFIG", err });
        }
        this.logStatus("set", "return", { err, data: value });
        cb?.(err, value);
        return { err, data: value };
    }
    async setMultiple(pathsAndValues, cb) {
        this.logStatus("setMultiple", "start", { pathsAndValues });
        await this.asyncConstructors.ready();
        const iocdFormat = pathsAndValues.map((entry) => {
            return {
                path: entry.field.join("."),
                value: entry.value
            };
        });
        const { err } = await toGoLang(() => this.glue.contexts.setPaths(FINSEMBLECONFIG, iocdFormat));
        this.logStatus("setMultiple", "start", { err, data: pathsAndValues });
        cb?.(err, pathsAndValues);
        return { err, data: pathsAndValues };
    }
    async remove(path, cb) {
        this.logStatus("remove", "start", { path });
        await this.asyncConstructors.ready();
        const err = null;
        let config = await this.glue.contexts.get(FINSEMBLECONFIG);
        if (!config) {
            config = {};
        }
        remove(path, config);
        await this.glue.contexts.set(FINSEMBLECONFIG, config);
        this.logStatus("remove", "return", { path });
        cb?.(err, path);
        return { err, data: path };
    }
    async removeMultiple(paths, cb) {
        this.logStatus("removeMultiple", "start", paths);
        await this.asyncConstructors.ready();
        const err = null;
        let config = await this.glue.contexts.get(FINSEMBLECONFIG);
        if (!config) {
            config = {};
        }
        paths.forEach((path) => {
            let pointer = config;
            for (let i = 0; i < path.length - 1; i++) {
                const field = path[i];
                if (!pointer[field]) {
                    pointer[field] = {};
                }
                pointer = pointer[field];
            }
            delete pointer[path[path.length - 1]];
        });
        await this.glue.contexts.set(FINSEMBLECONFIG, config);
        this.logStatus("removeMultiple", "return", { err, data: paths });
        cb?.(err, paths);
        return { err, data: paths };
    }
}
